import { useState } from "react";
import axios from "axios";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth/complete-access-recovery`;

function recoveryError(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join("; ") || "Recovery failed.";
  }
  if (!error.response) return "The recovery service could not be reached.";
  return "Recovery failed.";
}

export default function PlatformOwnerRecoveryPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmation) {
      setError("New password confirmation does not match.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(API, {
        email,
        recovery_code: recoveryCode,
        new_password: newPassword,
      });
      setRecoveryCode("");
      setNewPassword("");
      setConfirmation("");
      setComplete(true);
    } catch (requestError) {
      setError(recoveryError(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  if (complete) {
    return (
      <main className="login-page" data-testid="platform-owner-recovery-complete">
        <section className="login-brand-panel">
          <div className="login-brand-mark">J</div>
          <p className="operations-kicker">JFM Operations</p>
          <h1>Platform Owner recovery complete.</h1>
          <p>Your previous Platform Owner sessions and remaining recovery codes were revoked.</p>
        </section>
        <section className="login-form-panel">
          <div>
            <ShieldCheck size={22} />
            <h2>Password reset complete</h2>
            <p>Sign in again with your new password. Create a fresh recovery-code set after you sign in.</p>
            <button data-testid="platform-recovery-return-login" onClick={() => navigate("/login", { replace: true })} type="button">
              <KeyRound size={16} />
              Return to sign in
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="login-page" data-testid="platform-owner-recovery-page">
      <section className="login-brand-panel">
        <div className="login-brand-mark">J</div>
        <p className="operations-kicker">JFM Operations</p>
        <h1>Recover Platform Owner access.</h1>
        <p>Use one of the one-time codes you saved earlier. This process does not send a password by email.</p>
      </section>
      <section className="login-form-panel">
        <form data-testid="platform-owner-recovery-form" onSubmit={submit}>
          <div className="login-form-heading">
            <ShieldCheck size={20} />
            <div>
              <p className="operations-kicker">Emergency recovery</p>
              <h2>Reset the Platform Owner password</h2>
            </div>
          </div>
          <label htmlFor="platform-recovery-email">
            Platform Owner email
            <input
              autoComplete="email"
              data-testid="platform-owner-recovery-email"
              id="platform-recovery-email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          <label htmlFor="platform-recovery-code">
            One-time recovery code
            <input
              autoComplete="one-time-code"
              data-testid="platform-owner-recovery-code"
              id="platform-recovery-code"
              onChange={(event) => setRecoveryCode(event.target.value)}
              required
              spellCheck="false"
              value={recoveryCode}
            />
          </label>
          <label htmlFor="platform-recovery-new-password">
            New password
            <input
              autoComplete="new-password"
              data-testid="platform-owner-recovery-new-password"
              id="platform-recovery-new-password"
              minLength={12}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              type="password"
              value={newPassword}
            />
          </label>
          <label htmlFor="platform-recovery-confirm-password">
            Confirm new password
            <input
              autoComplete="new-password"
              data-testid="platform-owner-recovery-confirm-password"
              id="platform-recovery-confirm-password"
              minLength={12}
              onChange={(event) => setConfirmation(event.target.value)}
              required
              type="password"
              value={confirmation}
            />
          </label>
          {error && <p data-testid="platform-owner-recovery-error" role="alert">{error}</p>}
          <button data-testid="platform-owner-recovery-submit" disabled={submitting} type="submit">
            <KeyRound size={16} />
            {submitting ? "Recovering access…" : "Reset password with recovery code"}
          </button>
          <button data-testid="platform-owner-recovery-cancel" onClick={() => navigate("/login")} type="button">
            Back to sign in
          </button>
        </form>
      </section>
    </main>
  );
}
